import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// Common styled components for axis elements
const AxisLabel = styled.text`
  font-size: 12px;
  font-weight: 500;
  fill: var(--text-secondary);
`;

const XAxisLabel = styled(AxisLabel)`
  text-anchor: middle;
`;

// Modified YAxisLabel to handle text truncation
const YAxisLabel = styled(AxisLabel)`
  text-anchor: start;
  dominant-baseline: middle;
  cursor: default;
`;

const AxisTitle = styled.text`
  font-size: 14px;
  font-weight: 600;
  fill: var(--text-primary);
  text-anchor: middle;
`;

const SearchContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    padding: 1.25rem;
  }
`;

const SearchInput = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;

  @media (min-width: 768px) {
    flex: 1;
    max-width: 350px;
  }
`;

const StyledInput = styled.input`
  padding: 0.5rem 0.75rem;
  padding-left: 2.25rem;
  padding-right: ${(props) => (props.value ? "2.25rem" : "0.75rem")};
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 100%;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--chart-primary);
    box-shadow: 0 0 0 1px var(--chart-primary-translucent);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 50%;

  &:hover {
    background-color: var(--button-secondary-bg);
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--chart-primary-translucent);
  }
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--input-bg);
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }

  &:focus {
    outline: none;
    border-color: var(--chart-primary);
    box-shadow: 0 0 0 1px var(--chart-primary-translucent);
  }
`;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  padding-bottom: 20px;
  flex-direction: column;
  background-color: var(--card-bg);
  border-radius: var(--border-radius);
  overflow: hidden;
`;

const FixedHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: var(--card-bg);
  z-index: 15;
  padding: 0.75rem 0;
  display: flex;
  justify-content: start;
  align-items: start;
  margin-top: 2rem;
  width: 100%;
`;

const ChartPlaceholder = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 400px;
  background: var(--card-highlight-bg);
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  box-sizing: border-box;
  position: relative;

  /* Enhanced WebKit scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--card-bg);
    border-radius: 6px;
    margin: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--chart-primary-translucent);
    border-radius: 6px;
    border: 2px solid var(--card-bg);
    min-height: 40px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--chart-primary);
    cursor: pointer;
  }

  &::-webkit-scrollbar-corner {
    background: var(--card-bg);
  }

  /* Firefox scrollbar styles */
  scrollbar-width: thin;
  scrollbar-color: var(--chart-primary-translucent) var(--card-bg);

  /* Add smooth scrolling for better UX */
  scroll-behavior: smooth;
`;

const ScrollableChartContent = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: start;
  padding: 0 10px 20px 0;
  position: relative;
`;

const TooltipContainer = styled.div`
  position: absolute;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-md);
  pointer-events: none;
  z-index: 30;
  opacity: 0;
  transition: opacity 0.2s ease;

  &.visible {
    opacity: 1;
  }

  .tooltip-username {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }

  .tooltip-name {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 0.25rem;
  }

  .tooltip-count {
    font-size: 0.875rem;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
  }

  .tooltip-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    margin-right: 0.5rem;
    background-color: var(--chart-primary);
  }
`;

// New tooltip for displaying full username on hover
const UsernameTooltipContainer = styled.div`
  position: absolute;
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-md);
  pointer-events: none;
  z-index: 31;
  opacity: 0;
  transition: opacity 0.2s ease;
  max-width: 300px;
  color: var(--text-primary);
  font-size: 0.875rem;

  &.visible {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  text-align: center;
`;

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-secondary);

  svg {
    margin-bottom: 1rem;
    color: var(--text-tertiary);
  }

  p {
    font-size: 0.875rem;
    margin: 0;
  }

  .search-term {
    font-weight: 600;
    color: var(--text-primary);
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--card-bg);
  opacity: 0.9;
  z-index: 25;
  border-radius: var(--border-radius);
`;

// Function to truncate text with ellipsis
const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

// Function to format username for display
const formatUsername = (username) => {
  if (!username) return "";

  // If username already includes @, use it as is
  if (username.includes("@")) {
    return username;
  }

  // Otherwise add @ prefix
  return username;
};

// Function to get display username (handling truncation)
const getDisplayUsername = (username, maxLength) => {
  if (!username) return "";

  if (username.includes("@")) {
    const parts = username.split("@");
    return truncateText(parts[0], maxLength - 1);
  }

  return truncateText(username, maxLength - 1);
};

const useResponsiveChart = (containerRef) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.max(300, width), // Minimum width of 300px
          height
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);
    window.addEventListener("resize", updateDimensions);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      window.removeEventListener("resize", updateDimensions);
    };
  }, [containerRef]);

  return dimensions;
};

const UserOrdersChart = ({ userData, isLoading = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(userData || []);
  const [sortOrder, setSortOrder] = useState("ordersDESC");
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    username: "",
    name: "",
    orders: 0
  });

  const [usernameTooltipState, setUsernameTooltipState] = useState({
    visible: false,
    x: 0,
    y: 0,
    fullUsername: ""
  });

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const usernameTooltipRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const dimensions = useResponsiveChart(containerRef);

  useEffect(() => {
    if (!userData) return;

    let filtered;
    if (searchTerm.trim() === "") {
      filtered = [...userData];
    } else {
      filtered = userData.filter(
        (user) =>
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.name &&
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    sortData(filtered, sortOrder);
  }, [searchTerm, userData, sortOrder]);

  const getUserColor = (index) => {
    const colors = [
      { color: "#E3F2FD", textColor: "#1565C0", borderColor: "#90CAF9" }, // Light Blue
      { color: "#E8F5E9", textColor: "#2E7D32", borderColor: "#A5D6A7" }, // Light Green
      { color: "#FFF3E0", textColor: "#E65100", borderColor: "#FFCC80" }, // Light Orange
      { color: "#F3E5F5", textColor: "#6A1B9A", borderColor: "#CE93D8" }, // Light Purple
      { color: "#E1F5FE", textColor: "#0277BD", borderColor: "#81D4FA" }, // California - Light Blue
      { color: "#FFFDE7", textColor: "#F57F17", borderColor: "#FFF59D" }, // Light Yellow
      { color: "#E8EAF6", textColor: "#303F9F", borderColor: "#9FA8DA" }, // Light Indigo
      { color: "#FCE4EC", textColor: "#C2185B", borderColor: "#F48FB1" }, // Light Pink
      { color: "#F1F8E9", textColor: "#558B2F", borderColor: "#C5E1A5" }, // Light Green
      { color: "#FFEBEE", textColor: "#B71C1C", borderColor: "#EF9A9A" } // Light Red
    ];

    return colors[index % colors.length];
  };

  const sortData = (data, order) => {
    let sorted;
    switch (order) {
      case "ordersDESC":
        sorted = [...data].sort((a, b) => b.orders - a.orders);
        break;
      case "ordersASC":
        sorted = [...data].sort((a, b) => a.orders - b.orders);
        break;
      case "usernameASC":
        sorted = [...data].sort((a, b) => a.username.localeCompare(b.username));
        break;
      case "usernameDESC":
        sorted = [...data].sort((a, b) => b.username.localeCompare(a.username));
        break;
      default:
        sorted = [...data];
    }
    setFilteredData(sorted);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  // Handle bar hover for tooltip
  const handleBarHover = (event, user, i) => {
    if (!chartRef.current || !tooltipRef.current || !scrollContainerRef.current)
      return;

    // Get mouse position relative to chart
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    setTooltipState({
      visible: true,
      x: mouseX + 15, // Offset slightly from cursor
      y: mouseY,
      username: user.username,
      name: user.name || "No name provided",
      orders: user.orders,
      id: user.id
    });
  };

  const handleBarLeave = () => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  };

  // Handle username label hover for full username tooltip
  const handleUsernameHover = (event, username) => {
    if (!chartRef.current) return;

    // Get mouse position relative to chart container
    const chartRect = chartRef.current.getBoundingClientRect();
    const mouseX = event.clientX - chartRect.left;
    const mouseY = event.clientY - chartRect.top;

    // Set username tooltip state
    setUsernameTooltipState({
      visible: true,
      x: mouseX - 10,
      y: mouseY,
      fullUsername: formatUsername(username)
    });
  };

  const handleUsernameLeave = () => {
    setUsernameTooltipState((prev) => ({ ...prev, visible: false }));
  };

  if (!filteredData || filteredData.length === 0) {
    return (
      <ChartContainer ref={containerRef}>
        <SearchContainer>
          <SearchInput>
            <SearchIcon>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </SearchIcon>
            <StyledInput
              id="user-search"
              type="text"
              placeholder="Type to search users..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <ClearButton onClick={handleClearSearch}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </ClearButton>
            )}
          </SearchInput>

          <div>
            <SortSelect value={sortOrder} onChange={handleSortChange}>
              <option value="ordersDESC">Orders: High to Low</option>
              <option value="ordersASC">Orders: Low to High</option>
              <option value="usernameASC">Username: A to Z</option>
              <option value="usernameDESC">Username: Z to A</option>
            </SortSelect>
          </div>
        </SearchContainer>

        <ChartPlaceholder className="empty">
          <NoResults>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
            <p>
              No results found{searchTerm ? ` matching "${searchTerm}"` : ""}
            </p>
          </NoResults>
        </ChartPlaceholder>
      </ChartContainer>
    );
  }

  // Fixed width for username labels
  const FIXED_LABEL_WIDTH = 110;
  const MAX_CHARS = 12; // Maximum characters before truncation (including @)

  // Setup chart dimensions and scales
  const margin = {
    top: 20,
    right: 30,
    bottom: 50,
    left: FIXED_LABEL_WIDTH + 20
  };
  const chartWidth = dimensions.width || 600; // Fallback width if dimensions not ready
  const width = chartWidth - margin.left - margin.right;

  const barHeight = 30;
  const barPadding = 10;
  const barSpacing = barHeight + barPadding;

  const contentHeight = Math.max(
    350,
    filteredData.length * barSpacing + margin.top + margin.bottom
  );

  // Calculate scale for bar widths
  const maxOrders = Math.max(...filteredData.map((d) => d.orders), 1);
  const xScale = (value) => (value / maxOrders) * width;
  const yScale = (index) => margin.top + index * barSpacing;

  // Generate evenly spaced tick values
  const tickCount = 5;
  const xTicks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round((maxOrders / tickCount) * i)
  );

  return (
    <ChartContainer ref={containerRef}>
      <SearchContainer>
        <SearchInput>
          <SearchIcon>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </SearchIcon>
          <StyledInput
            id="user-search"
            type="text"
            placeholder="Type to search users..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <ClearButton onClick={handleClearSearch}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </ClearButton>
          )}
        </SearchInput>

        <div>
          <SortSelect value={sortOrder} onChange={handleSortChange}>
            <option value="ordersDESC">Orders: High to Low</option>
            <option value="ordersASC">Orders: Low to High</option>
            <option value="usernameASC">Username: A to Z</option>
            <option value="usernameDESC">Username: Z to A</option>
          </SortSelect>
        </div>
      </SearchContainer>

      <FixedHeader>
        <div style={{ width: "100%", position: "relative" }}>
          <svg width="100%" height="40" style={{ overflow: "visible" }}>
            <g transform={`translate(${margin.left},15)`}>
              {/* X-Axis title */}
              <AxisTitle x={width / 2} y="-30">
                Number of Orders
              </AxisTitle>

              {/* X-Axis labels without lines */}
              {xTicks.map((tick, i) => (
                <g key={i}>
                  <XAxisLabel x={xScale(tick)} y="20">
                    {tick}
                  </XAxisLabel>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </FixedHeader>

      <ChartPlaceholder ref={scrollContainerRef}>
        {isLoading && (
          <LoadingOverlay>
            <div className="loading-spinner" />
          </LoadingOverlay>
        )}

        <ScrollableChartContent ref={chartRef}>
          <svg
            width="100%"
            height={contentHeight}
            viewBox={`0 0 ${chartWidth} ${contentHeight}`}
            preserveAspectRatio="xMinYMin meet"
          >
            <g transform={`translate(${margin.left},0)`}>
              {/* Horizontal grid lines */}
              {filteredData.map((_, i) => (
                <line
                  key={`grid-${i}`}
                  x1="0"
                  y1={yScale(i) + barHeight / 2}
                  x2={width}
                  y2={yScale(i) + barHeight / 2}
                  stroke="var(--border-color)"
                  strokeOpacity="0.2"
                  strokeDasharray="4,4"
                />
              ))}

              {/* Vertical grid lines */}
              {xTicks.map((tick, i) => (
                <line
                  key={`vgrid-${i}`}
                  x1={xScale(tick)}
                  y1="0"
                  x2={xScale(tick)}
                  y2={contentHeight - margin.bottom}
                  stroke="var(--border-color)"
                  strokeOpacity="0.2"
                  strokeDasharray="4,4"
                />
              ))}

              {/* Bars */}
              {filteredData.map((user, i) => {
                const colorInfo = getUserColor(i);
                const displayUsername = getDisplayUsername(
                  user.username,
                  MAX_CHARS
                );

                return (
                  <g key={i}>
                    {/* Background for the username label to make the clickable area more obvious */}
                    <rect
                      x={-FIXED_LABEL_WIDTH - 10}
                      y={yScale(i)}
                      width={FIXED_LABEL_WIDTH}
                      height={barHeight}
                      fill="transparent"
                      onMouseOver={(e) => handleUsernameHover(e, user.username)}
                      onMouseLeave={handleUsernameLeave}
                    />

                    {/* Username (y-axis) with fixed width and truncation */}
                    <YAxisLabel
                      x={-FIXED_LABEL_WIDTH - 5}
                      y={yScale(i) + barHeight / 2}
                      onMouseOver={(e) => handleUsernameHover(e, user.username)}
                      onMouseLeave={handleUsernameLeave}
                    >
                      {displayUsername.length > 10
                        ? displayUsername.slice(0, 10) + "..."
                        : displayUsername}
                    </YAxisLabel>

                    {/* Bar with direct mouse event handlers */}
                    <rect
                      x="0"
                      y={yScale(i)}
                      width={xScale(user.orders)}
                      height={barHeight}
                      fill={colorInfo.color}
                      rx="4"
                      opacity="0.85"
                      stroke={colorInfo.borderColor}
                      strokeWidth="1"
                      onMouseEnter={(e) => handleBarHover(e, user, i)}
                      onMouseLeave={handleBarLeave}
                    />

                    {/* Order count */}
                    <text
                      x={xScale(user.orders) + 8}
                      y={yScale(i) + barHeight / 2 + 4}
                      fontSize="13"
                      fill={colorInfo.textColor}
                      fontWeight="500"
                      pointerEvents="none" /* Prevent text from interfering with mouse events */
                    >
                      {user.orders}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Screen reader description */}
            <desc>
              Horizontal bar chart showing order frequency by user.
              {filteredData
                .map(
                  (user) => `User ${user.username} has ${user.orders} orders.`
                )
                .join(" ")}
            </desc>
          </svg>

          {/* Tooltip for bar hover */}
          <TooltipContainer
            ref={tooltipRef}
            className={tooltipState.visible ? "visible" : ""}
            style={{
              left: `${tooltipState.x}px`,
              top: `${tooltipState.y}px`,
              transform: "translateY(-50%)"
            }}
          >
            <div className="tooltip-username">
              {formatUsername(tooltipState.username)}
            </div>
            {tooltipState.name && tooltipState.name !== "No name provided" && (
              <div className="tooltip-name">{tooltipState.name}</div>
            )}
            <div className="tooltip-count">
              <div
                className="tooltip-color"
                // style={{
                //   backgroundColor: getUserColor(
                //     filteredData.findIndex(u => u.username === tooltipState.username)
                //   ).color
                // }}
              />
              <span>{tooltipState.orders} Orders</span>
            </div>
          </TooltipContainer>

          {/* Username tooltip for showing full username on hover */}
          <UsernameTooltipContainer
            ref={usernameTooltipRef}
            className={usernameTooltipState.visible ? "visible" : ""}
            style={{
              position: "absolute",
              left: `${usernameTooltipState.x}px`,
              top: `${usernameTooltipState.y}px`,
              transform: "translateY(-50%)"
            }}
          >
            {usernameTooltipState.fullUsername}
          </UsernameTooltipContainer>
        </ScrollableChartContent>
      </ChartPlaceholder>
    </ChartContainer>
  );
};

export default UserOrdersChart;
